import { readMarketingConsent } from "@/components/analytics/consent";
import { recordAnalyticsDebugEvent } from "@/components/analytics/debug-events";
import type { AnalyticsEventName } from "@/components/analytics/event-catalog";

const COACHING_LEAD_SEND_TO = "AW-16798392352/Utg_CKmjs_McEKDAjMo-";
const PENDING_REGISTRATION_KEY = "thecoach_pending_registration";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function canSendAnalyticsEvent(
  name: AnalyticsEventName,
  params: Record<string, unknown>,
) {
  recordAnalyticsDebugEvent(name, params);
  return !import.meta.env.DEV && readMarketingConsent() === "granted";
}

export function trackCoachingLead() {
  const params = { content_name: "Koçluk Başvurusu" };
  if (!canSendAnalyticsEvent("coaching_lead", params)) return;

  window.gtag?.("event", "conversion", {
    send_to: COACHING_LEAD_SEND_TO,
    value: 1,
    currency: "TRY",
  });
  window.gtag?.("event", "generate_lead", {
    lead_source: "coaching_application",
  });
  window.fbq?.("track", "Lead", params);
}

export function trackCoachingFormStart(packageTitle: string) {
  const params = {
    form_name: "coaching_application",
    package_name: packageTitle,
  };
  if (!canSendAnalyticsEvent("form_start", params)) return;

  window.gtag?.("event", "form_start", params);
  window.fbq?.("trackCustom", "CoachingFormStart", params);
}

export function trackCoachingFormSubmit(packageTitle: string) {
  const params = {
    form_name: "coaching_application",
    package_name: packageTitle,
  };
  if (!canSendAnalyticsEvent("form_submit", params)) return;

  window.gtag?.("event", "form_submit", params);
  window.fbq?.("trackCustom", "CoachingFormSubmit", params);
}

export function trackCoachingFormError(packageTitle: string) {
  const params = {
    form_name: "coaching_application",
    package_name: packageTitle,
  };
  if (!canSendAnalyticsEvent("form_error", params)) return;

  window.gtag?.("event", "form_error", params);
  window.fbq?.("trackCustom", "CoachingFormError", params);
}

export function trackAccountRegistration(method: "email" | "google") {
  const params = { method };
  if (!canSendAnalyticsEvent("sign_up", params)) return;

  window.gtag?.("event", "sign_up", params);
  window.fbq?.("track", "CompleteRegistration", {
    content_name: "Hesap Oluşturma",
    registration_method: method,
  });
}

export function trackLoginAttempt(
  method: "password" | "magic_link" | "google",
) {
  const params = { method };
  if (!canSendAnalyticsEvent("login_attempt", params)) return;

  window.gtag?.("event", "login_attempt", params);
  window.fbq?.("trackCustom", "LoginAttempt", params);
}

export function trackLoginSuccess(method: "password") {
  const params = { method };
  if (!canSendAnalyticsEvent("login", params)) return;

  window.gtag?.("event", "login", params);
  window.fbq?.("trackCustom", "LoginSuccess", params);
}

export function trackLoginError(method: "password" | "magic_link") {
  const params = { method };
  if (!canSendAnalyticsEvent("login_error", params)) return;

  window.gtag?.("event", "login_error", params);
  window.fbq?.("trackCustom", "LoginError", params);
}

export function trackMagicLinkSent() {
  const params = { method: "magic_link" };
  if (!canSendAnalyticsEvent("login_link_sent", params)) return;

  window.gtag?.("event", "login_link_sent", params);
  window.fbq?.("trackCustom", "LoginLinkSent", params);
}

export function trackProgramJoinAttempt(source: "dialog" | "invite_page") {
  const params = { join_source: source };
  if (!canSendAnalyticsEvent("join_program_attempt", params)) return;

  window.gtag?.("event", "join_program_attempt", params);
  window.fbq?.("trackCustom", "JoinProgramAttempt", params);
}

export function trackProgramJoinError() {
  const params = { join_source: "invite_page" };
  if (!canSendAnalyticsEvent("join_program_error", params)) return;

  window.gtag?.("event", "join_program_error", params);
  window.fbq?.("trackCustom", "JoinProgramError", params);
}

export function markPendingAccountRegistration(method: "google") {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(PENDING_REGISTRATION_KEY, method);
}

export function trackPendingAccountRegistration() {
  if (typeof window === "undefined") return;
  const method = window.sessionStorage.getItem(PENDING_REGISTRATION_KEY);
  if (method !== "google") return;

  window.sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
  trackAccountRegistration(method);
}

export function trackProgramStart(programId: string, programName: string) {
  const params = { program_id: programId, program_name: programName };
  if (!canSendAnalyticsEvent("start_program", params)) return;

  window.gtag?.("event", "start_program", params);
  window.fbq?.("trackCustom", "StartProgram", params);
}

export function trackCalculatorComplete(calculatorName: string) {
  const params = { calculator_name: calculatorName };
  if (!canSendAnalyticsEvent("calculator_complete", params)) return;

  window.gtag?.("event", "calculator_complete", params);
  window.fbq?.("trackCustom", "CalculatorComplete", params);
}

export function trackContentView(
  contentType: "essay" | "projects",
  contentId: string,
) {
  const params = { content_type: contentType, content_id: contentId };
  if (!canSendAnalyticsEvent("view_content", params)) return;

  window.gtag?.("event", "view_content", params);
  window.fbq?.("track", "ViewContent", {
    content_type: contentType,
    content_ids: [contentId],
  });
}

export function trackProjectSupport(projectId: string, projectTitle: string) {
  const params = { project_id: projectId, project_name: projectTitle };
  if (!canSendAnalyticsEvent("support_project", params)) return;

  window.gtag?.("event", "support_project", params);
  window.fbq?.("trackCustom", "SupportProject", params);
}

export function trackContentEngagement(contentId: string) {
  const params = {
    content_type: "essay",
    content_id: contentId,
    engagement_time_seconds: 30,
  };
  if (!canSendAnalyticsEvent("content_engaged", params)) return;

  window.gtag?.("event", "content_engaged", params);
  window.fbq?.("trackCustom", "ContentEngaged", params);
}

export function trackCtaClick(ctaName: string, destination?: string) {
  const params = {
    cta_name: ctaName,
    ...(destination ? { destination } : {}),
  };
  if (!canSendAnalyticsEvent("cta_click", params)) return;

  window.gtag?.("event", "cta_click", params);
  window.fbq?.("trackCustom", "CtaClick", params);
}

export function trackCoachingDurationSelect(durationMonths: number) {
  const params = { duration_months: durationMonths };
  if (!canSendAnalyticsEvent("select_coaching_duration", params)) return;

  window.gtag?.("event", "select_coaching_duration", params);
  window.fbq?.("trackCustom", "SelectCoachingDuration", params);
}

export function trackContactClick(
  contactMethod: "email" | "phone" | "whatsapp",
) {
  const params = { contact_method: contactMethod };
  if (!canSendAnalyticsEvent("contact_click", params)) return;

  window.gtag?.("event", "contact_click", params);
  window.fbq?.("track", "Contact", params);
}

export function trackSocialClick(platform: string, destination: string) {
  const params = { platform, destination };
  if (!canSendAnalyticsEvent("social_click", params)) return;

  window.gtag?.("event", "social_click", params);
}
